import { ApplicationRef, ComponentRef, ElementRef, Injectable, Injector, ViewContainerRef} from '@angular/core';
import { BehaviorSubject, Observable } from "rxjs";
import { TourBubbleComponent } from '@generic/tour-bubble/tour-bubble.component';
/**
 * TourStep interface
 * @param stepId - The step id
 * @param title - The step title
 * @param content - The step content
 * @param element - The step element
 * @param position - The step position
 * @param end - Whether the step is the last step
 * @param fastForward - Whether the step will be automatically skipped if the next step element is present
 * @param canSkip - Whether the step can be skipped
 */
export interface TourStep {
  stepId?: string;
  title?: string;
  content?: string;
  element?: any;
  position?: string;
  end?: boolean;
  fastForward?: boolean;
  canSkip?: boolean;
}

/**
 * TourService
 * @description This service is used to manage the tours.
 * This service is used along with the tourTarget directive to render the tour bubble attached to the target element with these attributes:
 *  tourId{string} - The tour id
 *  stepId{string} - The step id
 *  tourPosition{string} - The position of the bubble relative to the target element - will override the position of the step in provided in defineTour method
 * *  See the directive for more specific guidelines
 * 
 * To create a tour, you need to define the steps and the tour id and pass them into the defineTour method.
 * 
 * To start a tour, you need to call the startTour method with the tour id.
 * 
 * To end a tour, you need to call the endTour method with the tour id.
 * 
 * To advance to the next step, you need to call the nextStep method with the tour id and the current step id. 
 * This can be triggered by any user interaction that has a definable event
 * ie in the click event of a component: this.tourService.nextStep('my-tour', 'click-button-step');
 * 
 * To remove a tour element, you need to call the removeTourElement method with the tour id and the step id.
 * 
 */

@Injectable({
  providedIn: 'root'
})
export class TourService {
  private activeTour: string;
  private tours: Map<string, TourStep[]> = new Map<string, TourStep[]>();
  private currentStep: BehaviorSubject<TourStep | null> = new BehaviorSubject<TourStep | null>(null);
  private storedSteps:any = {};
  private componentRef: ComponentRef<TourBubbleComponent>;
  private viewContainerRef: ViewContainerRef;
  private bubbleHistory = [];
  constructor(      
    private appRef: ApplicationRef,
    private injector: Injector) { }

  /**
   * Defines a tour with the given steps.
   * @param tourId - The tour id
   * @param steps - The steps of the tour
   */
  public defineTour(tourId: string, steps: TourStep[]): void {
    if (this.tours.has(tourId)) {
      let existingSteps = this.tours.get(tourId);
      steps.forEach(step => {
        if (!existingSteps.some(existingStep => existingStep.stepId === step.stepId)) {
          existingSteps.push(step);
        }
      });
      this.tours.set(tourId, existingSteps);
    } else {
      this.tours.set(tourId, steps);
    }
  }

  /**
   * Starts the tour with the given tour id.
   * @param tourId - The tour id
   */
  public startTour(tourId: string): void {
    this.currentStep.next(null);
    this.activeTour = tourId;
    this.nextStep(tourId);
  }

  /**
   * Ends the tour with the given tour id.
   * @param tourId - The tour id
   */
  endTour(tourId: string){
    this.activeTour = null;
    this.currentStep.next(null);
    this.destroyTourBubble();
  }

  /**
   * Advances to the next step of the tour with the given tour id.
   * @param tourId - The tour id
   * @param currentStepId - The current step id
   */
  public nextStep(tourId: string, currentStepId?: string): void {
    if(!tourId){
      return;
    }
    //support for csv for tours and steps
    let tours = tourId.split(',');
    let currentSteps = currentStepId?.split(',') || [];
    let active = false

    tours.forEach((tour, index) => {
      if(this.activeTour === tour){
        active = true;
      }
    });
    if(!active){
      return;
    }
    tourId = this.activeTour;
    currentStepId = currentSteps[tours.indexOf(tourId)];
    const steps = this.tours.get(tourId);
    const currentStep = this.currentStep.value;
    if (steps && (currentStepId === undefined || currentStep?.stepId === currentStepId)) {
      const currentIndex = currentStep ? steps.findIndex(step => step.stepId === currentStep.stepId) : -1;
      const nextStep = steps[currentIndex + 1];
      if (nextStep?.end) {
        this.currentStep.next(null);
        this.destroyTourBubble();
      } else if (nextStep) {        
        this.currentStep.next(nextStep);
        if(this.checkFastForward(tourId)){
          return this.nextStep(tourId);
        }
        this.destroyTourBubble();
        console.log('NEXT STEP', nextStep);
        if(nextStep.element){
          this.renderCurrentStep(tourId);
        }
      } else {
        this.destroyTourBubble();
        //await a new step
      }
    }
  }
  /**
   * Gets the current step of the tour with the given tour id.
   * @param tourId - The tour id
   */
  public getCurrentStep(): Observable<TourStep | null> {
    return this.currentStep.asObservable();
  }

  /**
   * Checks if the next step of the tour with the given tour id can be skipped.
   * @param tourId - The tour id
   */
  checkFastForward(tourId: string) {
    const steps = this.tours.get(tourId);
    const currentStep = this.currentStep.value;
    const currentIndex = steps.findIndex(step => step.stepId === currentStep.stepId);
    const nextStep = steps[currentIndex + 1];
    if (nextStep?.element && currentStep.fastForward) {
      return true;
    }
    return false;
  }

  public renderCurrentStep(tourId: string): void {
    const currentStepValue = this.currentStep.value;
    this.createTourBubble(tourId, currentStepValue);
  }

  registerTourStep(tourId: string, stepId: string, element: ElementRef, position: string) {
    const steps = this.tours.get(tourId);
    if (steps) {
      const step = steps.find(step => step.stepId === stepId);
      if (step) {
        step.element = element;
        if(position){
          step.position = position;
        }
      }
    }else{
      if(!this.storedSteps[tourId]){
        this.storedSteps[tourId] = {};
      }
      this.storedSteps[tourId][stepId] = {element, position};
    }
    if (this.currentStep.value?.stepId === stepId)
    {
      this.renderCurrentStep(tourId);
    }
  }

  removeTourElement(tourId: string, stepId: string) {
    if (tourId && stepId) {
      if (!this.tours.has(tourId)) {
        return;
      }
      const steps = this.tours.get(tourId);
      const step = steps.find(step => step.stepId === stepId);
      const currentStep = this.currentStep.value;
      if (currentStep?.stepId === stepId) {
        this.destroyTourBubble();
      }
      step.element = null;
    }
  }

  registerContainerRef(containerRef: ViewContainerRef){
    this.viewContainerRef = containerRef;
  }


  private createTourBubble(tourId: string, step: TourStep): void {
    //investigat why a random unit test unrelated to this component fails when this.viewContainerRef is null
    if(!this.viewContainerRef){
      return;
    }
    console.log('CREATE TOUR BUBBLE', step);
    this.destroyTourBubble();
    setTimeout(() => {
      this.componentRef = this.viewContainerRef.createComponent(TourBubbleComponent);
      this.bubbleHistory.push(this.componentRef);
      this.componentRef.instance.tourId = tourId;
      this.componentRef.instance.step = step;
      this.componentRef.changeDetectorRef.detectChanges();  // Trigger change detection
      
    }, 300);
  }

  private destroyTourBubble(): void {
    if (this.componentRef) {
      const componentRef = this.componentRef;
      const instance = componentRef.instance;
      instance.animationState = 'leave';  // Assuming you have an animation state control
      componentRef.changeDetectorRef.detectChanges();  // Allow animation to start

      // Delay the destruction to allow the leave animation to play
      setTimeout(() => {
        this.appRef.detachView(componentRef.hostView);
        componentRef.destroy(); 
        this.clearBubbleHistory();
      }, 300);  // Adjust time based on your animation duration
    }
  }

  clearBubbleHistory() {
    this.bubbleHistory.forEach(bubble => {
      if (bubble) {
        try {
          const componentRef = bubble;
          const instance = componentRef.instance;
          instance.animationState = 'leave';  // Assuming you have an animation state control
          componentRef.changeDetectorRef.detectChanges();  // Allow animation to start

          // Delay the destruction to allow the leave animation to play
          setTimeout(() => {
            this.appRef.detachView(componentRef.hostView);
            componentRef.destroy();
          });  // Adjust time based on your animation duration
        } catch (e) {
        }
      }
    });
    this.bubbleHistory = [];
  }

}

